import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useAppDataContext } from '@cox/core-ui8/dist/AppDataContext'
import { ButtonTypes } from '@cox/core-ui8/dist/Button'

import { LinkDetails, ReactStickyProps } from './types'
import { INVERSION_PAGE_TYPES, STICKY_OVERLAY_ZINDEX } from '../../constants'
import useClickAwayListener from '../../hooks/useClickAwayListener'
import { useInversionData } from '../../hooks/useInversionData'
import { appendHash, getSlugfromUrl } from '../../utils/helper-util'
import { INVERSION_CB_SERVICES_CITY_DEFAULT, INVERSION_RESI_SERVICES_CITY_DEFAULT } from '../../utils/inversion-util'
import { getImageSrc } from '../../utils/local-util'
import { ScreenSizes } from '../../utils/types'
import ReactButton from '../ReactButton'
import ReactText from '../ReactText'
import IsInEditorText from '../shared/IsInEditorText'
import { Accordion } from '../shared/ReactBootstrap'
import './ReactSticky.scss'
import ReactOverlay from '../shared/ReactOverlay'

const ReactSticky = (props: ReactStickyProps) => {
  const {
    text,
    anchor,
    openInNewTab,
    stickyText,
    linkDetails,
    linkUrl,
    buttonStates,
    accessibilityLabel,
    accordionIcon,
    altText,
    isInEditor = false,
    linkType,
    linkId,
    lob = '',
    isSticky = true
  } = props

  // Always use secondary button style for sticky navigation
  const buttonTypes = ButtonTypes.SECONDARY
  const [isMobileScreen, setIsMobileScreen] = useState(false)
  const [isStickyNature, setIsStickyNature] = useState(false)
  const [applyOverlay, setApplyOverlay] = useState(false)
  const [linkPageDetails, setLinkPageDetails] = useState([])
  const hasButton = text && text.trim() !== ''
  const inversionItems = useInversionData()
  const firstItem = inversionItems?.[0] ?? {}
  const { templates = '' } = firstItem
  const currentTemplate = getSlugfromUrl(window.location.pathname)
  const templateValue = templates ?? ''
  const templatePageArr = templateValue !== 'NULL' && templateValue !== '' ? templateValue.split(',') : []
  const cb_indValues = templatePageArr.filter((page: string) => page.startsWith('Ind-'))
  const resi_intValues = templatePageArr.filter((page: string) => page.startsWith('Int-'))
  let templatePages = templatePageArr.filter((page: string) => !page.startsWith('Ind-') && !page.startsWith('Int-'))

  const { appData } = useAppDataContext()
  const { pageType = '', isLocal = false } = appData?.page ?? {}

  //If CB and Industries then show Ind- only
  if (isLocal === 'true' && pageType === INVERSION_PAGE_TYPES.cb_services_offers_ind) {
    templatePages = cb_indValues.map((page: string) => page.replace('Ind-', ''))
  }
  //If Residential and Internet then show Int- only
  if (isLocal === 'true' && pageType === INVERSION_PAGE_TYPES.services_offers_int) {
    templatePages = resi_intValues.map((page: string) => page.replace('Int-', ''))
  }

  const isLocalLogic =
    isLocal === 'true' &&
    (pageType.includes(INVERSION_PAGE_TYPES.services_city) || pageType.includes(INVERSION_PAGE_TYPES.services_offers))

  const getLinkDetails = () => {
    const linkDetailsInversion = templatePages?.map((page: string) => {
      const anchorVal =
        (pageType.includes('cb_') && page === INVERSION_CB_SERVICES_CITY_DEFAULT) ||
        (!pageType.includes('cb_') && page === INVERSION_RESI_SERVICES_CITY_DEFAULT)
          ? ''
          : page.toLowerCase().split(' ').join('-')
      return {
        linkText: page,
        anchor: anchorVal,
        openInNewTab: false
      }
    })
    setLinkPageDetails(isLocalLogic ? linkDetailsInversion : linkDetails)
  }
  useEffect(() => {
    getLinkDetails()
  }, [templates])

  const getUrl = (url: string, anchorId: string, index: number) => {
    let anchorUrl = ''
    const host = window.location.origin
    const currentPath = window.location.pathname.replace('.html', '')
    if (isLocalLogic) {
      if (pageType.includes(INVERSION_PAGE_TYPES.services_city)) {
        anchorUrl = index === 0 ? `${host}${currentPath}.html` : `${host}${currentPath}/${anchorId.toLowerCase()}.html`
      } else if (pageType === INVERSION_PAGE_TYPES.services_offers || pageType === INVERSION_PAGE_TYPES.cb_services_offers) {
        const backUrl = currentPath.split('/')
        backUrl.pop()
        anchorUrl =
          index === 0 && !anchorId
            ? `${host}${backUrl.join('/')}.html`
            : `${host}${backUrl.join('/')}/${anchorId.toLowerCase()}.html`
      } else if (
        pageType === INVERSION_PAGE_TYPES.cb_services_offers_ind ||
        pageType === INVERSION_PAGE_TYPES.services_offers_int
      ) {
        const backUrl = currentPath.split('/')
        if (backUrl.length > 7) {
          backUrl.pop()
        }
        anchorUrl =
          index === 0 && !anchorId
            ? `${host}${currentPath}.html`
            : `${host}${backUrl.join('/')}/${anchorId.toLowerCase()}.html`
      }
    } else {
      anchorUrl = appendHash(url, anchorId)
    }
    return encodeURI(anchorUrl)
  }
  const linkClick = (url: string, newTab: boolean, anchorId: string, index: number) => {
    const anchorUrl = getUrl(url, anchorId, index)
    if (anchorId || isLocalLogic) {
      newTab ? window.open(anchorUrl, '_blank') : window.open(anchorUrl, '_self')
      const stickyNav = document.querySelector('.react-sticky-container.is-sticky')
      const targetElement = document.querySelector(`[id="${anchorId}"]`)
      if (targetElement) {
        const y =
          targetElement?.getBoundingClientRect()?.top + window.scrollY - (stickyNav?.getBoundingClientRect()?.height || 120)
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    } else if (url) {
      newTab ? window.open(url, '_blank') : window.open(url, '_self')
    }
    hideOverlay()
  }
  const LinkDetails = ({ linkDetails }: { linkDetails: Array<LinkDetails> }) => {
    if (linkDetails?.length < 0) return null
    return (
      <>
        {linkDetails?.length > 0 &&
          linkDetails.map((link: LinkDetails, index) => {
            const { linkText = '', linkPath = '', openinnewtab = false, anchor = '' } = link
            return (
              <React.Fragment key={anchor + index}>
                {linkText && (
                  <a
                    href={getUrl(linkPath, anchor, index)}
                    role='button'
                    tabIndex={0}
                    aria-label={linkText}
                    title={linkText}
                    className={`link-text cox-text-interactive3 ${
                      isLocalLogic &&
                      (anchor === currentTemplate ||
                        (pageType.includes(INVERSION_PAGE_TYPES.services_city) && index === 0)) &&
                      'sticky-link--active'
                    }`}
                    onClick={() => linkClick(linkPath, openinnewtab, anchor, index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        linkClick(linkPath, openinnewtab, anchor, index)
                      }
                    }}
                  >
                    {linkText}
                  </a>
                )}
              </React.Fragment>
            )
          })}
      </>
    )
  }

  const stickyRef = useRef(null)

  const resizeSticky = useCallback(() => {
    window.innerWidth < ScreenSizes.XL ? setIsMobileScreen(true) : (setIsMobileScreen(false), setApplyOverlay(false))
  }, [])
  const applyStickyPosition = useCallback(() => {
    const stickyElement = stickyRef?.current as unknown as Element
    const compTop = stickyElement?.getBoundingClientRect().top
    if (!isLocalLogic) window.scrollY > compTop ? setIsStickyNature(isSticky) : setIsStickyNature(false)
  }, [])

  useEffect(() => {
    resizeSticky()
    window.addEventListener('scroll', applyStickyPosition)
    window.addEventListener('resize', resizeSticky)
    return () => {
      window.removeEventListener('scroll', applyStickyPosition)
      window.removeEventListener('resize', resizeSticky)
    }
  }, [applyStickyPosition, resizeSticky])

  const LinksRenderer = () => {
    const mobileClass = isMobileScreen ? '-mobile' : ''
    const centerClass = !hasButton && !stickyText && !isMobileScreen ? ' justify-content-center' : ''

    return (
      <div className={`links-button-container${mobileClass}${centerClass}`}>
        <div className={`links-container${mobileClass}${centerClass}`}>
          <LinkDetails linkDetails={linkPageDetails} />
        </div>
        {hasButton && (
          <div className='content-button'>
            <ReactButton
              text={text}
              buttonStates={buttonStates}
              buttonTypes={buttonTypes}
              id='sticky-navigation-button'
              anchor={anchor}
              aria-label={accessibilityLabel}
              linkType={linkType}
              linkId={linkId}
              linkUrl={linkUrl}
              openInNewTab={openInNewTab}
              size={'small'}
            />
          </div>
        )}
      </div>
    )
  }

  const handleClickAway = async () => {
    // TODO: Refactor without using document selector
    const button = document.querySelector('#react-sticky .accordion-button') as HTMLElement
    const buttonaria = document.querySelector('#react-sticky button[aria-expanded="true"]')
    const accordionBody = document.querySelector('#react-sticky .accordion-collapse')
    await button?.classList.add('collapsed')
    await buttonaria?.setAttribute('aria-expanded', 'false')
    await accordionBody?.classList.remove('show')
    button && buttonaria && button?.click()
  }
  const clickAwayRef = useClickAwayListener(handleClickAway)

  const showOverlay = () => {
    setApplyOverlay(true)
    /* setApplyOverlay((applyOverlay: boolean) => {
      if (!applyOverlay) {
        // TODO: Refactor without using document selector
        const button = document.querySelector('#react-sticky .accordion-button') as HTMLElement
        const buttonaria = document.querySelector('#react-sticky button[aria-expanded="false"]')
        const accordionBody = document.querySelector('#react-sticky .accordion-collapse')
        button?.classList.remove('collapsed')
        buttonaria?.setAttribute('aria-expanded', 'true')
        accordionBody?.classList.add('show')
      }
      return !applyOverlay
    }) */
  }
  const hideOverlay = () => {
    setApplyOverlay(false)
  }
  const StickyContentContainer = () => {
    const expanded = applyOverlay ? 'expanded' : ''
    const displayText = isMobileScreen && (!stickyText || stickyText.trim() === '') ? 'On this page' : stickyText
    const noStickyTextClass = !stickyText && hasButton ? ' no-sticky-text' : ''

    return isMobileScreen ? (
      <div className={`sticky-mobile ${expanded}`} id={'react-sticky'}>
        <div className={`accordion-container ${expanded}`}>
          <Accordion ref={clickAwayRef} defaultActiveKey={applyOverlay ? '0' : null}>
            <Accordion.Item eventKey='0'>
              <Accordion.Header as={'h3'}>
                <div className='sticky-text'>
                  {displayText && <ReactText text={displayText} />}
                  <img src={getImageSrc(accordionIcon as string)} className='accordion-chevron' aria-label={altText} />
                </div>
              </Accordion.Header>
              <Accordion.Body onEntered={showOverlay} onExited={hideOverlay}>
                <LinksRenderer />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
      </div>
    ) : (
      <div className={`content-container ${isStickyNature ? 'is-sticky' : ''} container${noStickyTextClass}`}>
        {stickyText && (
          <div className='sticky-text'>
            <ReactText text={stickyText} />
          </div>
        )}
        <LinksRenderer />
      </div>
    )
  }

  return (
    <>
      {IsInEditorText(isInEditor, 'React Sticky')}
      {templatePages?.length === 0 && isLocalLogic ? null : (
        <div
          data-testid={`react-sticky-container`}
          className={`react-sticky-container ${lob} ${isStickyNature ? 'is-sticky' : ''}`}
          ref={stickyRef}
        >
          <StickyContentContainer />
          <ReactOverlay
            active={applyOverlay}
            onClick={hideOverlay}
            zIndex={STICKY_OVERLAY_ZINDEX}
            backgroundColor='rgba(69, 80, 81, 0.70)'
          />
        </div>
      )}
    </>
  )
}
export default ReactSticky
